import { zodResolver } from '@hookform/resolvers/zod';
import { MobileDateTimePicker } from '@mui/x-date-pickers';
import { addDays } from 'date-fns';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogActions } from 'src/components/molecules/dialog';
import { z } from 'zod';

import { useExtendVisit } from '@/features/visits/hooks/mutation/use-extend-visit';

interface FormValues {
  finishesAt: Date;
}

const defaultValues: FormValues = {
  finishesAt: new Date(),
};

interface Props extends DialogActions {
  visitFinishesAt: Date;
  visitId: number;
}

export const ExtendVisitDialog = ({ onCancel, open, visitFinishesAt, visitId }: Props) => {
  const { t } = useTranslation(['visit']);

  const { mutate, isPending } = useExtendVisit(onCancel);

  const validationSchema = z.object({
    finishesAt: z.date(),
  });

  const {
    control,
    reset,
    handleSubmit,
    formState: { isValid, isDirty },
  } = useForm<FormValues>({
    resolver: zodResolver(validationSchema),
    defaultValues,
    mode: 'onBlur',
  });

  const onSubmit = (data: FormValues) => {
    mutate({ visitId, to: data.finishesAt });
  };

  useEffect(() => {
    if (!open) {
      reset(defaultValues);
    }
    if (open && visitFinishesAt) {
      reset({ finishesAt: visitFinishesAt });
    }
  }, [open, visitFinishesAt]);

  return (
    <Dialog
      open={open}
      onCancel={onCancel}
      onConfirm={handleSubmit(onSubmit)}
      title={t('visit:extendVisitDialog.title')}
      variant="info"
      isConfirmButtonDisabled={!isValid || !isDirty}
      isConfirmButtonSubmitting={isPending}>
      <Controller
        name="finishesAt"
        control={control}
        render={({ fieldState: { invalid, error }, field: { name, onChange, ref, value } }) => (
          <MobileDateTimePicker
            slotProps={{
              textField: {
                helperText: error?.message,
                error: invalid,
                sx: { my: 2, width: '100%' },
              },
            }}
            label={t('visit:extendVisitDialog.date')}
            value={value}
            name={name}
            ref={ref}
            ampm={false}
            disablePast
            minDateTime={new Date(visitFinishesAt)}
            maxDateTime={addDays(new Date(visitFinishesAt), 1)}
            onChange={onChange}
          />
        )}
      />
    </Dialog>
  );
};
